export * from './dep';
import chalk from 'chalk';
import { uniq } from 'lodash-es';
import micromatch from 'micromatch';
import fs, { existsSync, readFileSync, unlinkSync, writeFileSync } from 'node:fs';
import { relative, resolve } from 'node:path';
import { Logger, mergeConfig, normalizePath, type PluginOption, ResolvedConfig, UserConfig, ViteDevServer } from 'vite';
import ConfigBuilder from './builder/ConfigBuilder.ts';
import { DEFAULT_ASSETS_RE } from './constants.ts';
import * as fusion from './dep';
import { prepareParams } from './params';
import { getArgsAfterDoubleDashes, parseArgv } from './runner/app';
import { expandModules, loadConfigFile, mustGetAvailableConfigFile } from './runner/config';
import { displayAvailableTasks } from './runner/describe.ts';
import { resolveAllTasksAsProcessors, selectRunningTasks } from './runner/tasks.ts';
import { FusionPlugin, FusionPluginOptions, FusionPluginOptionsUnresolved, LoadedConfigTask, WatchTask } from './types';
import { forceArray } from './utilities/arr.ts';
import { cleanFiles, copyFilesAndLog, linkFilesAndLog, moveFilesAndLog } from './utilities/fs.ts';
import { mergeOptions } from './utilities/utilities.ts';

let params = parseArgv(getArgsAfterDoubleDashes(process.argv));
prepareParams(params);

let serverRunning = false;
export let builder: ConfigBuilder;

const originalTasks = params._;
const extraVitePlugins: FusionPlugin[] = [];
const defaultFusionOptions: FusionPluginOptions = {
  chunkDir: 'chunks',
  chunkNameObfuscation: true,
};
let overrideFusionOptions: FusionPluginOptions = {};

export function useFusion(fusionOptions: FusionPluginOptionsUnresolved = {}, tasks?: string | string[]): PluginOption {
  let devServer: ViteDevServer;
  let logger: Logger;
  let resolvedConfig: ResolvedConfig;
  let exitHandlersBound = false;

  // Server
  let serverUrl: string = '';
  let serverRoot: string = '';

  // Options
  let resolvedOptions = mergeOptions(defaultFusionOptions, prepareFusionOptions(fusionOptions));

  if (
    typeof tasks === 'string'
    || (Array.isArray(tasks) && tasks.length > 0)
  ) {
    params._ = forceArray(tasks);
  } else {
    params._ = originalTasks;
  }

  resolvedOptions.cliParams = params = mergeOptions(params, resolvedOptions.cliParams);

  return [
    {
      name: 'fusion',
      configResolved(config) {
        resolvedConfig = config;

        logger = config.logger;

        // @ts-ignore
        config.plugins.push(...extraVitePlugins);

        for (const plugin of (config.plugins as FusionPlugin[])) {
          if ('buildConfig' in plugin) {
            plugin.buildConfig?.(builder);
          }
        }
      },
      async config(config, env) {
        let root: string;

        if (config.root) {
          root = resolve(config.root);
        } else {
          root = params.cwd || process.cwd();
        }

        delete config.root;
        // delete builder.config.root;

        process.chdir(root);

        // Now create builder
        builder = new ConfigBuilder(config, env, resolvedOptions);

        // Retrieve config file
        let tasks: Record<string, LoadedConfigTask>;

        if (typeof resolvedOptions.fusionfile === 'string' || !resolvedOptions.fusionfile) {
          params.config ??= resolvedOptions.fusionfile;
          const configFile = mustGetAvailableConfigFile(root, params);

          // Load config
          tasks = await loadConfigFile(configFile);
        } else if (typeof resolvedOptions.fusionfile === 'function') {
          tasks = expandModules(await resolvedOptions.fusionfile());
        } else {
          tasks = expandModules(resolvedOptions.fusionfile);
        }

        // Override options by fusionfile
        builder.fusionOptions = mergeOptions(builder.fusionOptions, overrideFusionOptions);

        // Describe tasks
        if (params.list) {
          await displayAvailableTasks(tasks);
          return;
        }

        // Select running tasks
        const selectedTasks = selectRunningTasks([...params._] as string[], tasks);

        const runningTasks = (await resolveAllTasksAsProcessors(selectedTasks));

        for (const taskName in runningTasks) {
          const processors = runningTasks[taskName];

          for (const processor of processors) {
            await processor.config(taskName, builder);
          }
        }

        builder.merge(ConfigBuilder.globalOverrideConfig);
        builder.merge(builder.overrideConfig);

        // for (const plugin of plugins) {
        //   if (plugin.buildConfig) {
        //     await plugin.buildConfig(builder, env);
        //   }
        // }

        // console.log('plugin bottom', builder.config);
        //
        // show(builder.overrideConfig, 15)
        // show(builder.config, 15)

        return builder.config;
      },
      outputOptions(options) {
        // Protect upload folder
        if (resolvedConfig.build.emptyOutDir) {
          const dir = resolvedConfig.build.outDir;
          const uploadDir = resolve(dir, 'upload');

          if (existsSync(uploadDir)) {
            throw new Error(
              `The output directory: "${dir}" contains an "upload" folder, please move this folder away or set an different fusion outDir.`
            );
          }
        }
      },
      async buildStart(options) {
        if (builder.cleans.length > 0 && resolvedConfig.command !== 'serve') {
          await cleanFiles(builder.cleans, resolvedConfig.build.outDir || process.cwd());
        }
      },

      // Server
      configureServer(server) {
        builder.server = server;
        server.httpServer?.once('listening', async () => {
          // Build listening Host
          const scheme = server.config.server.https ? 'https' : 'http';
          const address = server.httpServer?.address();
          let host = address && typeof address !== 'string' ? address.address : 'localhost';
          const port = address && typeof address !== 'string' ? address.port : 80;

          if (host === '::1') {
            host = `[${host}]`;
          }

          const url = `${scheme}://${host}:${port}/`;
          serverUrl = url;
          serverRoot = server.config.root;

          // Build & write server file
          const serverFile = resolve(
            server.config.root,
            resolvedOptions.cliParams?.serverFile ?? 'tmp/vite-server'
          );
          const serverFileFull = resolve(server.config.root, serverFile);
          const pidFile = resolve(
            server.config.root,
            resolvedOptions.cliParams?.pidFile ?? 'tmp/vite-pid'
          );
          const anotherServerRunning = existsSync(serverFileFull) || existsSync(pidFile);

          await sleep(500);

          if (!serverRunning && anotherServerRunning) {
            console.log(chalk.yellow(`There may be a dev server running!`));
            console.log(`The server host file: ${chalk.cyan(serverFile)} or PID file: ${chalk.cyan(pidFile)} exists.`);
            console.log('Do you want to kill other process and start a new server? [N/y]');

            const answer = await new Promise<string>((resolve) => {
              process.stdin.once('data', (data) => {
                resolve(data.toString().trim());
              });
            });

            if (answer.toLowerCase() === 'y') {
              if (existsSync(pidFile)) {
                const pid = readFileSync(pidFile, 'utf-8');

                try {
                  process.kill(parseInt(pid), 'SIGTERM');
                  console.log(`Killed process with PID: ${chalk.yellow(pid)}`);
                } catch (err) {
                  console.log(`Failed to kill process with PID: ${chalk.yellow(pid)}. It may have already exited.`);
                }
              }

              unlinkSync(serverFileFull);
              unlinkSync(pidFile);
              console.log(`Start running new server on: ${chalk.green(url)} and PID: ${chalk.green(process.pid)}`);
            } else {
              console.log(chalk.yellow('Aborting server start.'));
              process.exit(0);
            }
          }

          serverRunning = true;

          await sleep(300);

          writeFileSync(serverFileFull, url);
          writeFileSync(pidFile, process.pid.toString());

          // Bind exit signals
          if (!exitHandlersBound) {
            process.on("exit", () => {
              for (const callback of builder.serverStopCallbacks) {
                callback(resolvedConfig, server);
              }

              if (fs.existsSync(serverFile)) {
                fs.rmSync(serverFile);
              }

              if (fs.existsSync(pidFile)) {
                fs.rmSync(pidFile);
              }
            });
            process.on("SIGINT", () => process.exit());
            process.on("SIGTERM", () => process.exit());
            process.on("SIGHUP", () => process.exit());
            exitHandlersBound = true;
          }
        });

        // const fullReloadWatches: string[] = [];
        // const customWatches: Exclude<WatchTask, string>[] = [];

        const checkReload = (event: 'add' | 'change') => {
          return (path: string) => {
            const fullReloadWatches: string[] = [];

            for (const watchTask of builder.watches) {
              // Pre-save full-reload paths
              if (typeof watchTask === 'string') {
                const file = resolve(watchTask).replace(/\\/g, '/');
                fullReloadWatches.push(file);
                server.watcher.add(file);
                continue;
              }

              if (watchTask.file === path) {
                handleCustomWatchReload(server, watchTask, logger);
              }
            }

            // Full-reload
            if (micromatch.isMatch(path, fullReloadWatches)) {
              server.ws.send({ type: 'full-reload', path: '*' });

              logger.info(
                `${chalk.green('full reload')} ${chalk.dim(relative(process.cwd(), path))}`,
                { timestamp: true }
              );
            }
          };
        };

        server.watcher.on('add', checkReload('add'));
        server.watcher.on('change', checkReload('change'));

        // for (const watchTask of builder.watches) {
        //   if (typeof watchTask === 'string') {
        //     const file = resolve(watchTask).replace(/\\/g, '/');
        //     fullReloadWatches.push(file);
        //     server.watcher.add(file);
        //     continue;
        //   }
        //
        //   const file = resolve(watchTask.file).replace(/\\/g, '/');
        //   customWatches.push(watchTask);
        //   server.watcher.add(file);
        // }
      },
      // async handleHotUpdate(ctx) {
      //   if (builder.watches.includes(ctx.file)) {
      //     if (ctx.modules.length > 0) {
      //       return ctx.modules;
      //     }
      //
      //     const modules = ctx.server.moduleGraph.getModulesByFile(ctx.file);
      //
      //     if (modules) {
      //       return [...modules];
      //     }
      //
      //     // const resolved = await ctx.server.pluginContainer.resolveId(ctx.file);
      //     // if (resolved) {
      //     //   const vm = server.moduleGraph.getModuleById(resolved.id) || server.moduleGraph.getModuleById(virtualPrefixId)
      //     //   if (vm) {
      //     //     return [vm]
      //     //   }
      //     // }
      //
      //     ctx.server.ws.send({ type: 'full-reload', path: '*' })
      //
      //     return [];
      //   }
      // }
    },
    {
      name: 'fusion:pre-handles',
      enforce: 'pre',
      async resolveId(source, importer, options) {
        for (const resolveId of builder.resolveIdCallbacks) {
          if (typeof resolveId !== 'function') {
            continue;
          }

          const result = await resolveId.call(this, source, importer, options);

          if (result) {
            return result;
          }
        }

        if (source.startsWith('hidden:')) {
          return source;
        }
      },
      async load(source, options) {
        for (const load of builder.loadCallbacks) {
          if (typeof load !== 'function') {
            continue;
          }

          const result = await load.call(this, source, options);

          if (result) {
            return result;
          }
        }

        if (source.startsWith('hidden:')) {
          return '';
        }
      },
      async transform(code, id, options) {
        for (const callback of builder.transformCallbacks) {
          if (typeof callback !== 'function') {
            continue;
          }

          const result = await callback.call(this, code, id, options);

          if (result) {
            return result;
          }
        }
      },
    },
    {
      name: 'fusion:post-handles',
      generateBundle(options, bundle) {
        for (const [fileName, chunk] of Object.entries(bundle)) {
          if (chunk.type === 'chunk' && chunk.facadeModuleId?.startsWith('hidden:')) {
            delete bundle[fileName];
          }
        }
      },
      async writeBundle(options, bundle) {
        const outDir = resolvedConfig.build.outDir || process.cwd();

        // Todo: override logger to replace vite's files logs
        // @see https://github.com/windwalker-io/core/issues/1355
        await moveFilesAndLog(builder.moveTasks, outDir, logger);
        await copyFilesAndLog(builder.copyTasks, outDir, logger);
        await linkFilesAndLog(builder.linkTasks, outDir, logger);

        for (const callback of builder.postBuildCallbacks) {
          await callback(options, bundle);
        }

        for (const [name, task] of builder.tasks) {
          for (const callback of task.postCallbacks) {
            await callback(options, bundle);
          }
        }
      },
    },
    {
      name: 'fusion:dev-asset-urls',
      enforce: 'pre',
      async transform(code, id) {
        // Only handle server url in dev server.
        if (!serverRunning) {
          return code;
        }

        const [path, query] = id.split('?');
        const params = new URLSearchParams(query);

        if (shouldBeAbsolute(id, params)) {
          const resolvedUrl = serverUrl.slice(0, -1) + normalizePath(realpathToUrl(path));
          return `export default ${JSON.stringify(resolvedUrl)}`;
        }

        function realpathToUrl(path: string, base = '') {
          return normalizePath(path).replace(normalizePath(serverRoot), base);
        }

        function shouldBeAbsolute(id: string, params: URLSearchParams) {
          if (
            DEFAULT_ASSETS_RE.test(id)
            && !params.has('raw')
            && !params.has('inline')
          ) {
            return true;
          }

          if (params.has('full')) {
            params.delete('full');
            return true;
          }

          if (params.has('url')) {
            params.delete('url');
            return true;
          }

          return false;
        }
      }
    }
  ];
}

function handleCustomWatchReload(
  server: ViteDevServer,
  watchTask: Exclude<WatchTask, string>,
  logger: Logger
) {
  const mods = server.moduleGraph.getModulesByFile(watchTask.moduleFile);

  if (mods) {
    for (const mod of mods) {
      server.moduleGraph.invalidateModule(mod);
    }

    const updateType = watchTask.updateType;

    logger.info(
      `${chalk.green(updateType)} ${chalk.dim(relative(process.cwd(), watchTask.file))}`,
      { timestamp: true }
    );

    if (updateType === 'full-reload') {
      server.ws.send({ type: 'full-reload', path: '*' });
    } else {
      server.ws.send({
        type: 'update',
        updates: [...mods].map((m) => {
          let url = m.url;

          // remove query
          if (url.indexOf('?') !== -1) {
            url = url.substring(0, url.indexOf('?'));
          }

          return ({
            type: updateType,
            path: url,
            acceptedPath: url,
            timestamp: Date.now()
          });
        })
      });
    }
  }
}

function prepareFusionOptions(options: FusionPluginOptionsUnresolved): FusionPluginOptions {
  if (typeof options === 'string') {
    return {
      fusionfile: options,
    };
  }

  if (typeof options === 'function') {
    return {
      fusionfile: options,
    };
  }

  return options;
}

export function configureBuilder(handler: (builder: ConfigBuilder) => void) {
  handler(builder);
}

export function overrideViteConfig(config: UserConfig | null) {
  // if (config === null) {
  //   ConfigBuilder.globalOverrideConfig = {};
  //   return;
  // }
  //
  // ConfigBuilder.globalOverrideConfig = mergeConfig(ConfigBuilder.globalOverrideConfig, config);
  if (config === null) {
    builder.overrideConfig = {};
    return;
  }

  builder.overrideConfig = mergeConfig(builder.overrideConfig, config);
}

export function overrideOptions(options: FusionPluginOptions) {
  return overrideFusionOptions = mergeOptions(overrideFusionOptions, options);
}

export function outDir(outDir: string) {
  // ConfigBuilder.globalOverrideConfig = mergeConfig<UserConfig, UserConfig>(ConfigBuilder.globalOverrideConfig, {
  //   build: {
  //     outDir
  //   }
  // });
  builder.overrideConfig = mergeConfig<UserConfig, UserConfig>(builder.overrideConfig, {
    build: {
      outDir
    }
  });
}

export function chunkDir(dir: string) {
  builder.fusionOptions.chunkDir = dir;
}

export function alias(src: string, dest: string) {
  builder.overrideConfig = mergeConfig<UserConfig, UserConfig>(builder.overrideConfig, {
    resolve: {
      alias: {
        [src]: dest
      }
    }
  });
}

export function externals(...externals: (string | RegExp)[]) {
  builder.overrideConfig = mergeConfig<UserConfig, UserConfig>(builder.overrideConfig, {
    build: {
      rollupOptions: {
        external: externals,
      }
    }
  });
}

export function plugin(...plugins: FusionPlugin[]) {
  extraVitePlugins.push(...plugins);
}

export function clean(...paths: string[]) {
  builder.addCleans(...paths);

  builder.cleans = uniq(builder.cleans);
}

export function fullReloads(...paths: string[]) {
  builder.watches.push(...paths);

  builder.watches = uniq(builder.watches);
}

function sleep(ms: number) {
  return new Promise<void>(resolve => setTimeout(() => resolve(), ms));
}

export default {
  ...fusion,
  useFusion,
  configureBuilder,
  overrideViteConfig,
  overrideOptions,
  outDir,
  chunkDir,
  alias,
  externals,
  plugin,
  clean,
  fullReloads,
  params,
};
